/**
 * THE CALL-EVAL RUNNER (plan §12.3; owner decision 20, Q6c): a suite of real voice calls placed by
 * a machine caller for one eval journey, each scored on §12's dimensions, within a cost cap.
 *
 * THE SUITE prepares every line's audio once (`caller-audio.ts`) — no call time is spent waiting
 * on synthesis, and a source that cannot produce a line fails the suite before any call is placed
 * — then runs its runs one after another, each only while the suite's provider budget still holds
 * a whole run's worst case.
 *
 * ONE RUN, in order:
 * 1. the eval call is created through the eval call route (`POST /api/evals/calls`) with the
 *    runner's SDP offer and the cap's `max_call_seconds`; the media connects (`call-media.ts`) and
 *    `ready` lets the voice greet;
 * 2. the voice's greeting is let finish; then each line in turn: wait for the voice to finish its
 *    previous answer — or, for an interrupting line, barge in shortly after it starts — say the
 *    line in real time, and time the voice's first audio after it;
 * 3. after the last answer, the runner hangs up, and reads the call's record once the call has
 *    ended and its meter has settled (`GET /api/evals/calls/:call_id`);
 * 4. the run is scored (`score.ts`), and `finish` closes the call's eval conversation — also when
 *    the run failed or stopped early.
 * The runner hangs up mid-line or mid-wait when the operator interrupts (`CallEvalOptions.interrupted`,
 * checked first), when the cost cap says so (`cost-cap.ts`), or when the server ends the call (a
 * hold, a limit) and the media drops — every frame of a line and every poll of a wait asks; only
 * the pause before a barge-in does not.
 *
 * Every wait is bounded: the whole run is bounded by the cap's call length, and the reads after
 * it by `RECORD_SETTLE_TIMEOUT_MS`.
 */

import { fromIso } from '@wayai/core';
import { EVAL_CALL_SPOKEN_LINE_MAX_CHARS } from '@wayai/core/contracts';
import type {
  CallResponse,
  CreateEvalCallBody,
  CreateEvalCallResponse,
  EvalCallFinishResponse,
  EvalCallRecordResponse,
} from '@wayai/core/contracts';
import { extractApiMessage } from '../errors.js';
import { trailingSilenceMs, type CallerAudioSource, type CallerClip } from './caller-audio.js';
import type { CallMediaPort, MediaClock, SpeechOwedUntil, VoiceUtterance } from './call-media.js';
import { CostLedger, liveCapReason, providerUsd, type CapStopReason, type CostCap } from './cost-cap.js';
import {
  DEFAULT_THRESHOLDS,
  SCORE_DIMENSIONS,
  percentile,
  scoreRun,
  type LineTiming,
  type RunScore,
  type ScoreDimension,
  type ScoreThresholds,
} from './score.js';
import type { CallScript } from './script.js';

/** The eval call routes, as the runner calls them (the CLI's `ApiClient`). */
export interface EvalCallApi {
  createEvalCall(body: CreateEvalCallBody): Promise<CreateEvalCallResponse>;
  readyEvalCall(callId: string, hubId: string): Promise<CallResponse>;
  hangupEvalCall(callId: string, hubId: string): Promise<CallResponse>;
  getEvalCallRecord(callId: string, hubId: string, conversationId: string): Promise<EvalCallRecordResponse>;
  finishEvalCall(callId: string, hubId: string, conversationId: string): Promise<EvalCallFinishResponse>;
}

/** The one clock the runner and its media share: every time they compare is on it. */
export interface RunnerClock extends MediaClock {
  sleep(ms: number): Promise<void>;
}

export const systemRunnerClock: RunnerClock = {
  now: () => Date.now(),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

/**
 * The longest one of the voice's fixed lines takes to say when its builder wrote it: the
 * catalog's bound on those texts (`EVAL_CALL_SPOKEN_LINE_MAX_CHARS`) at about 15 characters a
 * second (pt-BR speech, plan §6). WayAI's own lines take a few seconds.
 */
export const LONGEST_SPOKEN_LINE_MS = Math.ceil(EVAL_CALL_SPOKEN_LINE_MAX_CHARS / 15) * 1000;

/** How long each wait on the voice may last; the defaults suit a real call. */
export interface RunnerTimings {
  /** The voice's greeting, from `ready`: it may be the builder's, as long as `LONGEST_SPOKEN_LINE_MS`. */
  greetingTimeoutMs: number;
  /** The voice's first audio after a line. */
  answerOnsetTimeoutMs: number;
  /**
   * The voice finishing an answer, from the line's end, or for a delegated line from the last
   * instant its answer was owed until (`delegatedAnswerOwedUntil`), when later. Both progress
   * cues before it may be the builder's, each as long as `LONGEST_SPOKEN_LINE_MS`.
   */
  answerDoneTimeoutMs: number;
  /**
   * Silence after the voice's audio that ends an answer the hub's agent was asked for: longer
   * than the gap between the voice's first words and its first progress cue, or between its two
   * cues (`PROGRESS_CUES_HEARD_AT_MS`). What a cue, or a silent wait, still owes after it is
   * `delegatedAnswerOwedUntil`'s.
   */
  delegatedAnswerQuietMs: number;
  /**
   * How long after a delegated line's answer timeout (`CreateEvalCallResponse.delegation_timeout_seconds`)
   * the timeout's line has surely been heard, when the answer never came: it is sent at the
   * deadline and heard about 0.6 s later, and call control's estimate of the caller's end of
   * speech, which the deadline counts from, can run about 0.5 s late (`call-control/delegation-loop.ts`).
   */
  answerTimeoutMarginMs: number;
  /** Silence that ends an answer the voice gives itself (a greeting, a thank-you). */
  ownAnswerQuietMs: number;
  /** An interrupting line starts this long after the voice starts its previous answer. */
  interruptAfterMs: number;
}

export const DEFAULT_RUNNER_TIMINGS: RunnerTimings = {
  greetingTimeoutMs: 20_000 + LONGEST_SPOKEN_LINE_MS,
  answerOnsetTimeoutMs: 20_000,
  answerDoneTimeoutMs: 45_000 + 2 * LONGEST_SPOKEN_LINE_MS,
  delegatedAnswerQuietMs: 5_500,
  answerTimeoutMarginMs: 3_000,
  ownAnswerQuietMs: 2_000,
  interruptAfterMs: 1_000,
};

/**
 * When call control's progress cues are heard, after the caller's end of speech, while an
 * answer is still owed (`PROGRESS_CUES_HEARD_AT_MS`, `call-control/delegation-loop.ts`).
 */
export const PROGRESS_CUES_HEARD_AT_MS = [5_000, 10_000] as const;

/**
 * How much earlier, and later, than those times the runner may hear a cue start. Call control
 * times each cue from its estimate of the caller's end of speech, measured within about 0.2 s
 * of the real end, or up to about 0.5 s late when only the delegation's arrival placed it; the
 * voice starts a cue up to about 0.15 s after it is due; each at p95 (`call-control/delegation-loop.ts`).
 * The cue's audio then takes its path back to the runner. The window is wider than all of
 * that, so a cue is not taken for the answer: an answer that happens to start within it is
 * waited past instead.
 */
export const CUE_HEARD_EARLY_MS = 1_000;
export const CUE_HEARD_LATE_MS = 2_000;

/**
 * A pause shorter than this keeps the voice's speech one stretch, so the pauses between the
 * sentences of one answer (the detector already joins those under 450 ms) do not start a new
 * thing said. WayAI's own first cue, about 2.5 to 3 s long, ends more than this before the
 * second is due; a builder's first cue over about 4 s may not.
 */
export const SPEECH_STRETCH_GAP_MS = 1_000;

/** When the voice's latest stretch of speech began: its last utterances, joined across pauses shorter than `SPEECH_STRETCH_GAP_MS`. */
function latestStretchBegan(said: readonly VoiceUtterance[]): number | null {
  let k = said.length - 1;
  if (k < 0) return null;
  while (k > 0 && said[k].startMs - (said[k - 1].endMs ?? said[k].startMs) < SPEECH_STRETCH_GAP_MS) k--;
  return said[k].startMs;
}

/**
 * Until when the voice may still owe the answer to a delegated line whose words ended at
 * `speechEnd`, given what it has said since. `answerDueMs` is how long after the caller's end of
 * speech the timeout's line has surely been heard, when the answer never came.
 * - Progress cues off: the wait is silent up to the timeout, so no pause before then says the
 *   answer is over.
 * - Cues on: call control says a cue only while the answer is owed, and after one the voice
 *   always speaks again: the next cue, the answer, or the timeout's line. So a stretch of speech
 *   begun within a cue's window may be that cue, and after it the answer is owed until the next
 *   cue's window has closed, or after the last cue, until the timeout's line is due. A stretch
 *   begun anywhere else is the answer, or the timeout's line, and owes nothing more, however many
 *   sentences it runs to. An answer that happens to begin within a cue's window, or less than the
 *   stretch gap after a cue ends, is so taken for that cue and waited past, never spoken over:
 *   for the last cue, until the timeout's line is due. Nothing is owed past the timeout's line,
 *   so the window of a cue that call control leaves out for a short timeout holds no wait beyond it.
 */
export function delegatedAnswerOwedUntil(speechEnd: number, progressCues: boolean, answerDueMs: number): SpeechOwedUntil {
  const dueAt = speechEnd + answerDueMs;
  if (!progressCues) return () => dueAt;
  return (said) => {
    const began = latestStretchBegan(said);
    if (began === null) return Number.NEGATIVE_INFINITY;
    const after = began - speechEnd;
    const cue = PROGRESS_CUES_HEARD_AT_MS.findIndex((at) => after >= at - CUE_HEARD_EARLY_MS && after <= at + CUE_HEARD_LATE_MS);
    if (cue < 0) return Number.NEGATIVE_INFINITY;
    const next: number | undefined = PROGRESS_CUES_HEARD_AT_MS[cue + 1];
    return next === undefined ? dueAt : Math.min(dueAt, speechEnd + next + CUE_HEARD_LATE_MS);
  };
}

/** How long the runner waits, after hanging up, for the call to end and its meter to settle. */
export const RECORD_SETTLE_TIMEOUT_MS = 30_000;
const RECORD_POLL_MS = 1_000;

export interface CallEvalOptions {
  api: EvalCallApi;
  hubId: string;
  script: CallScript;
  audio: CallerAudioSource;
  cap: CostCap;
  thresholds?: ScoreThresholds;
  timings?: Partial<RunnerTimings>;
  /** A fresh media connection for each run (a real `CallMedia`, or a test's fake). */
  createMedia: () => Promise<CallMediaPort>;
  clock?: RunnerClock;
  log?: (message: string) => void;
  /**
   * True once the operator asked to stop (Ctrl-C): the call in progress is hung up, read and
   * finished like any other stopped run, no call is placed after it, and no further run starts.
   */
  interrupted?: () => boolean;
}

export type RunStopReason = CapStopReason | 'call_ended' | 'interrupted';

export interface CallRunResult {
  run: number;
  call_id: string | null;
  conversation_id: string | null;
  /** `completed`: every line said; `stopped`: ended early by the cap, the server or an interrupt; `failed`: an error. */
  status: 'completed' | 'stopped' | 'failed';
  stopped_by: RunStopReason | null;
  error: string | null;
  lines_said: number;
  timings: LineTiming[];
  delegations_seen: number;
  record: EvalCallRecordResponse | null;
  score: RunScore | null;
  cost: {
    connected_ms: number;
    provider_usd: number;
    /** What the conversation's rows charged, read back after the call. */
    operations: number;
    /** False when the call meter had not billed the minutes by the time the record was read. */
    operations_settled: boolean;
  };
}

/** A run passes only when it said every line and every dimension passed. */
export function runPassed(run: CallRunResult): boolean {
  return run.status === 'completed' && run.score?.pass === true;
}

/** Every line's audio, in line order. Throws the source's own error for the first line it cannot produce. */
export function prepareClips(script: CallScript, audio: CallerAudioSource): Promise<CallerClip[]> {
  return Promise.all(script.lines.map((line) => audio.clipFor(line)));
}

/** The call's connected span from its record, or the runner's own measure when it has none. */
function connectedMs(record: EvalCallRecordResponse | null, measured: number): number {
  const from = record?.call.active_at ?? record?.call.started_at;
  const to = record?.call.ended_at;
  if (from && to) return Math.max(0, fromIso(to) - fromIso(from));
  return measured;
}

/** Place, drive, read and score one eval call with prepared clips. Never throws: a failure is the run's result. */
export async function runOneCall(options: CallEvalOptions, run: number, clips: readonly CallerClip[]): Promise<CallRunResult> {
  const clock = options.clock ?? systemRunnerClock;
  const timings = { ...DEFAULT_RUNNER_TIMINGS, ...options.timings };
  const log = options.log ?? (() => {});
  const { api, hubId, script, cap } = options;
  const interrupted = options.interrupted ?? (() => false);
  const result: CallRunResult = {
    run,
    call_id: null,
    conversation_id: null,
    status: 'completed',
    stopped_by: null,
    error: null,
    lines_said: 0,
    timings: [],
    delegations_seen: 0,
    record: null,
    score: null,
    cost: { connected_ms: 0, provider_usd: 0, operations: 0, operations_settled: false },
  };
  const quietAfter = (delegated: boolean) => (delegated ? timings.delegatedAnswerQuietMs : timings.ownAnswerQuietMs);
  const speechTailsMs = clips.map(trailingSilenceMs);

  let media: CallMediaPort | null = null;
  // The call's time is counted from before it is asked for: ahead of the meter's clock, never behind.
  let callStartedAt: number | null = null;
  let hungUp = false;
  try {
    media = await options.createMedia();
    const port = media;
    const sdpOffer = await port.offer();
    if (interrupted()) {
      result.stopped_by = 'interrupted';
      result.status = 'stopped';
      log(`run ${run}: interrupted before its call was placed`);
      return result;
    }
    const startedAt = clock.now();
    callStartedAt = startedAt;
    const created = await api.createEvalCall({
      hub_id: hubId,
      journey_id: script.journey_id,
      sdp_offer: sdpOffer,
      max_call_seconds: cap.maxCallSeconds,
    });
    result.call_id = created.call_id;
    result.conversation_id = created.conversation_id;
    log(`run ${run}: call ${created.call_id} placed (at most ${created.max_call_seconds} s)`);

    // How long a delegated line's answer may be owed, planned from the caller's end of speech. An
    // older backend that does not say is taken to fill the wait with cues, as it did, and its
    // waits end as they did.
    const progressCues = created.progress_cues !== false;
    const answerDueMs = Number.isFinite(created.delegation_timeout_seconds)
      ? created.delegation_timeout_seconds * 1000 + timings.answerTimeoutMarginMs
      : null;
    const answerOwedUntil = answerDueMs === null
      ? null
      : (speechEnd: number) => delegatedAnswerOwedUntil(speechEnd, progressCues, answerDueMs);
    if (!progressCues) {
      log(`run ${run}: progress cues are off: each delegated answer is awaited up to its ${created.delegation_timeout_seconds} s timeout`);
    }

    await port.connect(created.sdp_answer);
    const connectedAt = clock.now();
    await api.readyEvalCall(created.call_id, hubId);

    // Asked at every check of every wait, and before every frame of a line: ends the run when the
    // operator interrupted (checked first, so the wind-down never waits on another reason), on
    // the cap, or when the call dropped.
    const stop = (): boolean => {
      if (result.stopped_by) return true;
      if (interrupted()) {
        result.stopped_by = 'interrupted';
        return true;
      }
      if (!port.connected()) {
        result.stopped_by = 'call_ended';
        return true;
      }
      const reason = liveCapReason(cap, {
        delegationsSeen: port.delegationsSeen(),
        connectedMs: clock.now() - startedAt,
        minutePriceOps: created.minute_price_ops,
      });
      if (reason) result.stopped_by = reason;
      return reason !== null;
    };

    // The voice done with its answer to the line that ended at `line.endedAtMs`, its words at `speechEnd`.
    const waitForAnswer = (line: { endedAtMs: number; speechEnd: number; delegated: boolean }) => port.waitForVoiceDone(
      line.endedAtMs,
      quietAfter(line.delegated),
      timings.answerDoneTimeoutMs,
      stop,
      line.delegated && answerOwedUntil ? answerOwedUntil(line.speechEnd) : undefined,
    );

    await port.waitForVoiceDone(connectedAt, timings.ownAnswerQuietMs, timings.greetingTimeoutMs, stop);
    let previous = { endedAtMs: connectedAt, speechEnd: connectedAt, delegated: false };
    for (const [i, line] of script.lines.entries()) {
      if (stop()) break;
      if (line.interrupt) {
        const onset = await port.waitForVoiceOnset(previous.endedAtMs, timings.answerOnsetTimeoutMs, stop);
        if (onset !== null) await clock.sleep(Math.max(0, onset + timings.interruptAfterMs - clock.now()));
      } else if (i > 0) {
        await waitForAnswer(previous);
      }
      if (stop()) break;
      const span = await port.play(clips[i], stop);
      if (span.cut) break;
      result.lines_said += 1;
      const onset = await port.waitForVoiceOnset(span.endedAtMs, timings.answerOnsetTimeoutMs, stop);
      result.timings.push({ n: line.n, started_at_ms: span.startedAtMs, ended_at_ms: span.endedAtMs, voice_onset_ms: onset });
      // Call control times the cues from where the words stopped, not from the clip's silent tail.
      previous = { endedAtMs: span.endedAtMs, speechEnd: span.endedAtMs - speechTailsMs[i], delegated: line.expect_delegation };
    }
    if (!result.stopped_by) {
      await waitForAnswer(previous);
    }
    if (result.stopped_by) {
      result.status = 'stopped';
      log(`run ${run}: stopped early (${result.stopped_by}) after ${result.lines_said} line(s)`);
    }
  } catch (err) {
    result.status = 'failed';
    result.error = extractApiMessage(err);
  } finally {
    result.delegations_seen = media?.delegationsSeen() ?? 0;
    if (result.call_id) {
      try {
        await api.hangupEvalCall(result.call_id, hubId);
        hungUp = true;
      } catch (err) {
        log(`run ${run}: hang-up failed: ${extractApiMessage(err)}`);
      }
    }
    media?.close();
  }
  const measuredMs = callStartedAt === null ? 0 : clock.now() - callStartedAt;

  if (result.call_id && result.conversation_id) {
    const callId = result.call_id;
    const conversationId = result.conversation_id;
    try {
      result.record = await readSettledRecord(api, { callId, hubId, conversationId }, clock, hungUp);
    } catch (err) {
      result.error ??= `The call's record could not be read: ${extractApiMessage(err)}`;
    }
    if (result.record) {
      result.score = scoreRun(script, result.record, result.timings, options.thresholds ?? DEFAULT_THRESHOLDS);
      result.cost.operations = result.record.operations.charged;
      result.cost.operations_settled = result.record.operations.meter === 'billed';
    }
    try {
      await api.finishEvalCall(callId, hubId, conversationId);
    } catch (err) {
      log(`run ${run}: the call's conversation could not be closed: ${extractApiMessage(err)}`);
    }
  }
  result.cost.connected_ms = connectedMs(result.record, measuredMs);
  result.cost.provider_usd = providerUsd(result.cost.connected_ms, cap.providerUsdPerMinute);
  return result;
}

/**
 * The record once the call has ended and its meter has billed, or the last read at the deadline.
 * A meter left `pending` retries minutes later on its own (`conversation-call-meter.ts`), so the
 * runner stops reading there and reports the minutes as not settled.
 */
async function readSettledRecord(
  api: EvalCallApi,
  ids: { callId: string; hubId: string; conversationId: string },
  clock: RunnerClock,
  hungUp: boolean,
): Promise<EvalCallRecordResponse> {
  const deadline = clock.now() + (hungUp ? RECORD_SETTLE_TIMEOUT_MS : 0);
  for (;;) {
    const record = await api.getEvalCallRecord(ids.callId, ids.hubId, ids.conversationId);
    const ended = record.call.status === 'ended';
    if ((ended && record.operations.meter !== 'unmetered') || clock.now() >= deadline) return record;
    await clock.sleep(RECORD_POLL_MS);
  }
}

export interface DimensionRate {
  passed: number;
  scored: number;
}

export interface CallSuiteReport {
  journey: { id: string; name: string };
  audio: string;
  cap: CostCap;
  runs: CallRunResult[];
  /** Runs not started because the suite's budget would not hold another. */
  skipped_runs: number;
  /** The operator stopped the suite (Ctrl-C): the run in progress stopped, and later runs never started. */
  interrupted: boolean;
  pass_rate: DimensionRate;
  dimensions: Record<ScoreDimension, DimensionRate>;
  latency: { samples: number; p50_ms: number | null; p95_ms: number | null };
  cost: { provider_usd: number; operations: number };
}

/**
 * Run `runs` eval calls in turn, within the suite's budget, and aggregate their scores. Throws —
 * placing no call — when the caller's audio cannot be prepared.
 */
export async function runCallEvalSuite(options: CallEvalOptions & { runs: number }): Promise<CallSuiteReport> {
  const clips = await prepareClips(options.script, options.audio);
  const ledger = new CostLedger(options.cap);
  const log = options.log ?? (() => {});
  const runs: CallRunResult[] = [];
  let skipped = 0;
  const interrupted = options.interrupted ?? (() => false);
  for (let run = 1; run <= options.runs; run++) {
    if (interrupted()) break;
    if (!ledger.canStartRun()) {
      skipped = options.runs - run + 1;
      log(`suite budget reached: $${ledger.spentUsd.toFixed(2)} spent, a run may cost $${ledger.worstCaseRunUsd().toFixed(2)}; ${skipped} run(s) not started`);
      break;
    }
    const result = await runOneCall(options, run, clips);
    ledger.record({ providerUsd: result.cost.provider_usd, operations: result.cost.operations });
    runs.push(result);
    if (result.stopped_by === 'interrupted') break;
  }

  const scored = runs.filter((r) => r.score !== null);
  const dimensions = Object.fromEntries(SCORE_DIMENSIONS.map((name) => [name, {
    passed: scored.filter((r) => r.score!.passed[name]).length,
    scored: scored.length,
  }])) as Record<ScoreDimension, DimensionRate>;
  const samples = scored.flatMap((r) => r.score!.latency.samples_ms);
  return {
    journey: { id: options.script.journey_id, name: options.script.journey_name },
    audio: options.audio.describe,
    cap: options.cap,
    runs,
    skipped_runs: skipped,
    interrupted: interrupted(),
    pass_rate: { passed: runs.filter(runPassed).length, scored: runs.length },
    dimensions,
    latency: { samples: samples.length, p50_ms: percentile(samples, 50), p95_ms: percentile(samples, 95) },
    cost: { provider_usd: ledger.spentUsd, operations: ledger.spentOperations },
  };
}
